import { OpenSansText } from "@Components/CustomText/OpenSansText"
import { MultilineInput } from "@Components/Input/MutlilineInput"
import { Spacer } from "@Components/Space"
import { baseColors } from "@Constants/baseStyles"
import { currentDiagnosisAtom } from "@Recoil/Atom/DiagnosisAtom"
import { canGotoNextAtom, surveyResponsesAtom } from "@Recoil/Atom/surveyAtom"
import React, { useContext, useEffect, useState } from "react"
import { Dimensions, StyleSheet, TouchableOpacity, View } from "react-native"
import { RadioButton } from "react-native-paper"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { ParentSurveyContent } from "./ParentSurveyContent"
import { SALUTANCE } from "@Services/AxiosInstance"
import { SurveyContext } from "@Providers/SurveyProvider"

const { width } = Dimensions.get("screen")

export const QuestionYesOrNo = (props) => {
    const {
        axisId,
        surveyId,
        responseTypeId,
        hasPrecision,
        question,
        color,
        isChildren,
        questionNumber,
        responseIndex,
        userResponse,
        hasResponse,
        disabled,
        precisionText,
        isParent,
        setCurrentQuestionIndex,
        conditionToSkip,
        indexToSkip
    } = props

    const defaultResponse = {
        axisId,
        surveyId,
        responseTypeId,
        responseValue: "",
        hasPrecision,
        precisionAnswer: ""
    }

    const setCanGoToNext = useSetRecoilState(canGotoNextAtom)
    const { setIndexToSkip } = useContext(SurveyContext)
    const [response, setResponse] = useState(defaultResponse)
    const [responseGlobal, setResponseGlobal] = useRecoilState(surveyResponsesAtom)
    const [precision, setPrecision] = useState("")
    const currentEducationalDiagnosis = useRecoilValue(currentDiagnosisAtom)
    const [value, setValue] = useState(response.responseValue ?? "")

    useEffect(() => {
        const precisionLastValue =
            userResponse?.[responseIndex]?.precision ?? responseGlobal?.[response?.surveyId]?.precisionAnswer ?? ""
        // const responseLastValue =
        //     userResponse?.[responseIndex]?.precision ?? responseGlobal?.[response?.surveyId]?.precisionAnswer ?? ""
        setPrecision(precisionLastValue)
        setResponse({ ...defaultResponse })
    }, [hasResponse, userResponse, responseGlobal])

    useEffect(() => {
        setValue(
            userResponse?.[responseIndex]?.responseValue ?? responseGlobal?.[response?.surveyId]?.responseValue ?? ""
        )
    }, [responseIndex])

    useEffect(() => {
        if (userResponse?.id > 0 && currentEducationalDiagnosis?.id > 0 && !disabled) {
            setValue(Boolean(userResponse?.response))
            setPrecision(userResponse?.precision)
        }
    }, [userResponse])

    useEffect(() => {
        if (Boolean(SALUTANCE && value !== "" && conditionToSkip !== null)) {
            setIndexToSkip(() => {
                if ((typeof conditionToSkip === "number" ? Number(value) : Number(value) + "") === conditionToSkip) {
                    return indexToSkip
                }
                return 0
            })
        } else {
            setIndexToSkip(0)
        }
    }, [value, conditionToSkip, SALUTANCE])

    useEffect(() => {
        if (!disabled) {
            if (!(responseGlobal[surveyId]?.surveyId > 0)) {
                setResponseGlobal((prev) => ({
                    ...prev,
                    [surveyId]: { ...response, responseValue: value }
                }))
            }
        }
    }, [disabled])

    useEffect(() => {
        if (userResponse?.id > 0 && currentEducationalDiagnosis?.id > 0) {
            setValue(userResponse?.response)
        }
    }, [userResponse])

    useEffect(() => {
        setCanGoToNext(true)
    }, [])

    const handleChangeValue = (val) => {
        setValue(val)
        if (responseGlobal[surveyId]?.surveyId > 0) {
            setResponseGlobal((prev) => ({
                ...prev,
                [surveyId]: { ...responseGlobal[surveyId], responseValue: val }
            }))
        } else {
            setResponseGlobal((prev) => ({
                ...prev,
                [surveyId]: { ...response, responseValue: val }
            }))
        }
    }

    const handleChangePrecision = (textValue) => {
        setPrecision(textValue)
        if (responseGlobal[surveyId]) {
            setResponseGlobal((old) => ({
                ...old,
                [surveyId]: { ...responseGlobal[surveyId], precisionAnswer: textValue }
            }))
        } else {
            setResponseGlobal((old) => ({
                ...old,
                [surveyId]: { ...response, precisionAnswer: textValue }
            }))
        }
    }

    if (isParent) {
        return <ParentSurveyContent question={question} />
    }

    return (
        <View style={{ padding: 20 }}>
            <OpenSansText color={color}>
                {SALUTANCE ? "QUESTION : " : !isChildren ? `QUESTION N° ${questionNumber}` : ""}
            </OpenSansText>
            <OpenSansText style={{ marginTop: 10 }} fontSize={14}>
                {question}
            </OpenSansText>
            <Spacer height={20} />
            <View style={styles.radioGroupContainer}>
                <TouchableOpacity disabled={disabled} onPress={() => handleChangeValue(true)}>
                    <View style={{ flexDirection: "row", alignItems: "center" }}>
                        <RadioButton.Android
                            disabled={disabled}
                            color={baseColors.orange.dark}
                            onPress={() => handleChangeValue(true)}
                            value={value}
                            status={value ? "checked" : "unchecked"}
                        />
                        <OpenSansText fontSize={14}>Oui</OpenSansText>
                    </View>
                </TouchableOpacity>

                <TouchableOpacity
                    disabled={disabled}
                    onPress={() => handleChangeValue(false)}
                    style={{ marginLeft: 30 }}
                >
                    <View style={{ flexDirection: "row", alignItems: "center" }}>
                        <RadioButton.Android
                            disabled={disabled}
                            color={baseColors.orange.dark}
                            theme={{ colors: { primary: baseColors.orange.dark } }}
                            onPress={() => handleChangeValue(false)}
                            value={value}
                            status={value === false ? "checked" : "unchecked"}
                        />
                        <OpenSansText fontSize={14}>Non</OpenSansText>
                    </View>
                </TouchableOpacity>
            </View>

            {Boolean(hasPrecision) && (
                <>
                    <Spacer height={20} />
                    <OpenSansText fontSize={14}>{precisionText ?? "Si oui, précisez"} : </OpenSansText>
                    <Spacer height={20} />
                    <MultilineInput
                        disabled={disabled}
                        height={80}
                        value={precision}
                        onChange={(text) => handleChangePrecision(text)}
                    />
                </>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    radioGroupContainer: { flexDirection: "row", justifyContent: "space-between", width: width / 3 }
})
